import { useCallback } from "react";

import { ArrowsCounterClockwiseIcon, SphereIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import Chip from "@gx/dls/chips/Chip";
import FlexWrapper, { AlignItems, FlexDirection } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import Widget, { WidgetVariant } from "@gx/dls/widget/Widget";
import { SourceStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import SourceCardBody from "@/pages/sources/components/card/SourceCardBody";
import SourceCardFooter from "@/pages/sources/components/card/SourceCardFooter";
import SourceCardHeader from "@/pages/sources/components/card/SourceCardHeader";
import { SourceCardMetricsLoading } from "@/pages/sources/components/card/SourceCardLoading";

import { hydrateDeletedSourceName } from "@/utils/source";

export interface SourceCardProps {
  source: SourceResource;
  noBody?: boolean;
  noMetrics?: boolean;
  noFooter?: boolean;
}

const SourceCardMetrics = ({
  numProjects,
  numSyncs,
  isLoading,
}: {
  numProjects: bigint;
  numSyncs: bigint;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return <SourceCardMetricsLoading />;
  }

  return (
    <FlexWrapper gap={8} alignItems={AlignItems.CENTER}>
      <Chip icon={SphereIcon} label={Number(numProjects) || "—"} />
      <Chip icon={ArrowsCounterClockwiseIcon} label={Number(numSyncs) || "—"} />
    </FlexWrapper>
  );
};

const SourceCard = ({
  source,
  noBody = false,
  noMetrics = false,
  noFooter = false,
}: SourceCardProps) => {
  const navigate = useNavigate();

  const status = source.sourceStatus ?? SourceStatus.UNSPECIFIED;

  const handleSourceCardClick = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, sourceId: source.id }),
    });
  }, [source.id, navigate]);

  const name = source.isDeleted ? hydrateDeletedSourceName(source.name) : source.name;

  return (
    <FlexWrapper fillWidth>
      <Widget
        fillWidth
        variant={source.isDeleted ? WidgetVariant.ERROR : WidgetVariant.PRIMARY}
        onClick={source.isDeleted ? undefined : handleSourceCardClick}
      >
        <FlexWrapper gap={12} direction={FlexDirection.COLUMN}>
          <SourceCardHeader
            name={name}
            sourceType={source.sourceType}
            status={status}
            isStatusLoading={false}
            isDeleted={source.isDeleted}
          />
          {!noBody && <SourceCardBody lastSyncedAt={source.updatedAt} isLoading={false} />}
          {!noMetrics && (
            <SourceCardMetrics
              numProjects={source.metrics?.projectCount ?? 0n}
              numSyncs={source.metrics?.syncCount ?? 0n}
              isLoading={false}
            />
          )}
          {!noFooter && (
            <>
              <HorizontalDivider />
              <SourceCardFooter
                user={source.isDeleted ? source.deletedBy : source.createdBy}
                deletedAt={source.deletedAt}
                createdAt={source.isDeleted ? source.deletedAt : source.createdAt}
              />
            </>
          )}
        </FlexWrapper>
      </Widget>
    </FlexWrapper>
  );
};

export default SourceCard;
